import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { matchRoutes } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import './index.css'
import App from './App.jsx'
import { ROUTES, REDIRECTS } from './routes'

// V1 also installed a global fetch interceptor here (lib/maintenanceGuard)
// that routed a 503 from the API to a maintenance screen. The marketing
// site is static, has no API server of its own, and must stay reachable
// precisely WHEN the API is down (it's where a visitor goes to read the
// status), so there is nothing to guard. Dropped deliberately.

// ─────────────────────────────────────────────────────────────────────────────
// Resolve THIS page's route chunk before mounting. Not an optimisation.
// ─────────────────────────────────────────────────────────────────────────────
// Pages are code split (routes.jsx), and this app mounts with createRoot(), not
// hydrateRoot(): the client discards the prerendered markup and renders the tree
// from scratch. Those two facts collide. React clears the container on its first
// commit, so if the first render suspends on a route chunk, the commit is the
// Suspense FALLBACK — a page that had already painted its hero and headline from
// static HTML plus inlined critical CSS would go blank for a round trip and then
// come back. That is a visible flash and a CLS score, in exchange for nothing.
//
// So the chunk for the URL being visited is fetched FIRST, which flips that
// route's component to its synchronous path (routes.jsx `routeComponent`), and
// only then does the app mount. The fetch itself does not cost a round trip:
// prerender.js writes a <link rel="modulepreload"> for the matching chunk into
// every static page, so it is already in flight alongside the entry chunk.
//
// ⚠️ Deliberately NO timeout and no mount-anyway fallback. If the chunk fails or
// never arrives we leave the prerendered HTML exactly where it is, because that
// is strictly better than what mounting would do: the route would suspend, React
// would commit the empty fallback, and a readable page would become a blank one.
// Doing nothing keeps the copy, the headings, the images and every link working,
// and gives up only the interactive behaviour the missing chunk was carrying.
//
// Route matching reuses react-router's own matchRoutes rather than a hand rolled
// one, so `/legal/:slug` and route ranking behave here exactly as they will
// inside <Routes> a moment later. An unknown URL resolves the 404 chunk, because
// Caddy's try_files serves /index.html for a path it has no file for, so a bad
// deep link arrives carrying the homepage's bytes and the SPA has to sort it out.
function routeComponentFor(pathname) {
  const redirect = REDIRECTS.find((r) => r.from === pathname)
  const matches  = matchRoutes(
    ROUTES.map((r) => ({ path: r.path, row: r })),
    redirect ? redirect.to : pathname,
  )
  const row = matches?.[0]?.route?.row ?? ROUTES.find((r) => r.path === '/404')
  return row.Component
}

function mount() {
  createRoot(document.getElementById('root')).render(
    <StrictMode>
      <HelmetProvider>
        <App />
      </HelmetProvider>
    </StrictMode>,
  )
}

routeComponentFor(window.location.pathname).preload().then(mount, () => {
  // Swallowed on purpose. See the ⚠️ above: the static page stays up, and there
  // is nothing useful to tell the visitor about a chunk they never asked for.
})
